import { supabase } from '../services/supabase'
import type { DriverItem } from '../services/supabase'

/**
 * Upload de arquivo para o driver do usuário
 */
export async function uploadFile(
    file: File,
    currentPastaId: string | null,
    userId: string
): Promise<DriverItem> {
    console.log('📤 Upload de arquivo:', { fileName: file.name, currentPastaId, userId });

    // Gerar nome único para o arquivo
    const timestamp = Date.now()
    const randomStr = Math.random().toString(36).substring(2, 15)
    const fileExt = file.name.split('.').pop()
    const fileName = `${timestamp}-${randomStr}.${fileExt}`

    // Determinar caminho no storage
    let storagePath: string;

    if (currentPastaId) {
        // Buscar caminho da pasta atual
        console.log('📂 Buscando caminho da pasta atual:', currentPastaId);
        const { data: pasta, error: pastaError } = await supabase
            .from('driver_items')
            .select('caminho')
            .eq('id', currentPastaId)
            .eq('user_id', userId)
            .single()

        if (pastaError) {
            console.error('❌ Erro ao buscar pasta:', pastaError);
            throw pastaError
        }

        if (pasta && pasta.caminho) {
            storagePath = `${pasta.caminho}/${fileName}`;
            console.log('📂 Arquivo será salvo em:', storagePath);
        } else {
            // Fallback para raiz
            storagePath = `${userId}/${fileName}`;
            console.log('⚠️ Pasta não encontrada, salvando na raiz');
        }
    } else {
        // Upload na raiz
        storagePath = `${userId}/${fileName}`;
        console.log('📂 Upload na raiz:', storagePath);
    }

    console.log('📤 Caminho final do arquivo:', storagePath);

    // Fazer upload para o Supabase Storage
    const { error: uploadError } = await supabase.storage
        .from('user-driver')
        .upload(storagePath, file)

    if (uploadError) {
        console.error('❌ Erro no upload:', uploadError);
        throw uploadError
    }

    console.log('✅ Arquivo enviado para o storage com sucesso');

    // Obter URL pública
    const { data: urlData } = supabase.storage
        .from('user-driver')
        .getPublicUrl(storagePath)

    // Salvar metadados no banco
    const metadata = {
        user_id: userId,
        nome: file.name,
        tipo: 'arquivo',
        caminho: storagePath,
        tamanho: file.size,
        tipo_mime: file.type,
        extensao: fileExt,
        pai_id: currentPastaId,
        compartilhado: false,
        exige_senha: false
    }

    console.log('📝 Salvando metadados:', metadata);

    const { data, error } = await supabase
        .from('driver_items')
        .insert(metadata)
        .select()
        .single()

    if (error) {
        console.error('❌ Erro ao salvar metadados:', error);
        throw error
    }

    console.log('✅ Metadados salvos com sucesso:', data.id);

    // Adicionar URL ao resultado
    return { ...data, url_arquivo: urlData.publicUrl }
}

/**
 * Criar pasta no driver
 */
export async function createFolder(
    folderName: string,
    currentPastaId: string | null,
    userId: string
): Promise<string> {
    console.log('📁 createFolder chamada:', { folderName, currentPastaId, userId });

    // Gerar caminho da pasta para exibição (manter nome original com espaços)
    const caminhoExibicao = folderName.trim();

    // Caminho para storage (normalizar)
    let caminhoStorage = folderName.trim().toLowerCase().replace(/\s+/g, '-')

    // Se houver pasta pai, buscar seu caminho
    if (currentPastaId) {
        console.log('📂 Buscando pasta pai:', currentPastaId);
        const { data: pai, error: paiError } = await supabase
            .from('driver_items')
            .select('caminho')
            .eq('id', currentPastaId)
            .eq('user_id', userId)
            .single()

        if (paiError) {
            console.error('❌ Erro ao buscar pasta pai:', paiError);
            throw paiError
        }

        if (pai) {
            caminhoStorage = `${pai.caminho}/${caminhoStorage}`
            console.log('📂 Caminho da pasta pai:', pai.caminho);
        }
    } else {
        // Se é na raiz, usar user_id como prefixo
        caminhoStorage = `${userId}/${caminhoStorage}`
        console.log('📂 Criando pasta na raiz');
    }

    console.log('📁 Caminho final da pasta:', caminhoStorage);

    // Criar registro da pasta
    const metadata: any = {
        user_id: userId,
        nome: caminhoExibicao,
        tipo: 'pasta',
        caminho: caminhoStorage,
        compartilhado: false,
        exige_senha: false
    };

    // Só adicionar pai_id se não for null
    if (currentPastaId) {
        metadata.pai_id = currentPastaId;
    }

    // Criar pasta "física" no storage (upload de arquivo vazio .keep)
    const folderPlaceholder = `${caminhoStorage}/.keep`;
    console.log('📦 Criando placeholder da pasta no storage:', folderPlaceholder);

    try {
        const { error: storageError } = await supabase.storage
            .from('user-driver')
            .upload(folderPlaceholder, new Blob([''], { type: 'text/plain' }))

        if (storageError && !storageError.message.includes('already exists')) {
            console.warn('⚠️ Erro ao criar placeholder no storage (ignorado):', storageError);
        }
    } catch (err) {
        console.warn('⚠️ Erro ao criar placeholder (ignorado):', err);
    }

    console.log('📝 Metadados da pasta:', metadata);

    // Verificar sessão antes de inserir
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
    console.log('🔐 Sessão atual:', sessionData?.session?.user?.id);
    console.log('👤 User ID usado:', userId);

    if (sessionError) {
        console.error('❌ Erro ao verificar sessão:', sessionError);
        throw sessionError
    }

    if (!sessionData?.session) {
        console.error('❌ Não há sessão ativa');
        throw new Error('Não há sessão ativa. Por favor, faça login novamente.')
    }

    const { data, error } = await supabase
        .from('driver_items')
        .insert(metadata)
        .select('id')
        .single()

    if (error) {
        console.error('❌ Erro ao inserir pasta no banco:', error);
        console.error('❌ Código do erro:', error.code);
        console.error('❌ Mensagem:', error.message);
        console.error('❌ Detalhes:', error.details);
        console.error('❌ Hint:', error.hint);
        throw error
    }

    console.log('✅ Pasta criada com ID:', data.id);
    return data.id
}

/**
 * Calcular espaço usado pelo usuário
 */
export async function calculateSpaceUsed(): Promise<number> {
    // Calcular diretamente pela query
    const { data, error } = await supabase
        .from('driver_items')
        .select('tamanho')
        .eq('tipo', 'arquivo')

    if (error) {
        console.error('❌ Erro ao calcular espaço usado:', error);
        return 0;
    }

    const total = (data || []).reduce((sum, item) => sum + (item.tamanho || 0), 0);
    console.log('💾 Espaço total usado:', total, 'bytes (', (total / (1024 * 1024)).toFixed(2), 'MB)');
    return total;
}

/**
 * Deletar arquivo ou pasta
 */
export async function deleteItem(itemId: string, itemPath: string, tipo: string): Promise<void> {
    console.log('🗑️ Deletando item:', { itemId, itemPath, tipo });

    // Se for arquivo, deletar do storage
    if (tipo === 'arquivo') {
        const { error: storageError } = await supabase.storage
            .from('user-driver')
            .remove([itemPath])

        if (storageError) {
            console.error('Erro ao deletar do storage:', storageError)
        }
    } else if (tipo === 'pasta') {
        // Se for pasta, deletar o arquivo .keep do storage
        const folderPlaceholder = `${itemPath}/.keep`;
        const { error: storageError } = await supabase.storage
            .from('user-driver')
            .remove([folderPlaceholder])

        if (storageError) {
            console.warn('⚠️ Erro ao deletar placeholder da pasta (ignorado):', storageError);
        }
    }

    // Deletar do banco (se for pasta, RLS vai deletar filhos também)
    const { error } = await supabase
        .from('driver_items')
        .delete()
        .eq('id', itemId)

    if (error) throw error

    console.log('✅ Item deletado com sucesso');
}

/**
 * Renomear arquivo ou pasta
 */
export async function renameItem(itemId: string, novoNome: string): Promise<void> {
    console.log('✏️ Renomeando item:', { itemId, novoNome });

    const { error } = await supabase
        .from('driver_items')
        .update({
            nome: novoNome.trim(),
            atualizado_em: new Date().toISOString()
        })
        .eq('id', itemId)

    if (error) {
        console.error('❌ Erro ao renomear:', error);
        throw error
    }

    console.log('✅ Item renomeado com sucesso');
}

/**
 * Baixar arquivo
 */
export async function downloadFile(itemPath: string, fileName: string): Promise<void> {
    const { data, error } = await supabase.storage
        .from('user-driver')
        .download(itemPath)

    if (error) throw error

    // Criar link de download
    const url = URL.createObjectURL(data)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

/**
 * Compartilhar arquivo/pasta
 */
export async function shareItem(
    itemId: string,
    password: string | null
): Promise<{ token: string; url: string }> {
    // Gerar token único
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let token = ''
    for (let i = 0; i < 32; i++) {
        token += chars.charAt(Math.floor(Math.random() * chars.length))
    }

    // Atualizar item no banco
    const { error } = await supabase
        .from('driver_items')
        .update({
            compartilhado: true,
            compartilhamento_token: token,
            senha_compartilhamento: password,
            exige_senha: !!password
        })
        .eq('id', itemId)

    if (error) throw error

    const url = `${window.location.origin}/drive/shared/${token}`

    return { token, url }
}

/**
 * Alternar proteção com senha de arquivo ou pasta
 */
export async function toggleProtection(itemId: string, isProtected: boolean, password?: string): Promise<void> {
    console.log('🔒 Alternando proteção:', { itemId, isProtected, hasPassword: !!password });

    const updateData: any = {
        exige_senha: !isProtected
    };

    if (password) {
        updateData.senha_compartilhamento = password;
    } else if (!isProtected) {
        updateData.senha_compartilhamento = null;
    }

    const { error } = await supabase
        .from('driver_items')
        .update(updateData)
        .eq('id', itemId)

    if (error) {
        console.error('❌ Erro ao alternar proteção:', error);
        throw error
    }

    console.log('✅ Proteção alternada com sucesso');
}

/**
 * Formata bytes para formato legível
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

